import React, {useState, useRef} from 'react';
import styled, {css} from 'styled-components';
import {AutoHeightParent} from '@bootcamp/web/src/components/AutoHeightParent';

import {useHistory} from 'react-router-dom';

import {H2} from '@bootcamp/web/src/components/Typography/next';
import {Heading, SubTitle, Blurb, BlurbText, Table, TableRow, TableOverflowOuterWrapper, TableOverflowInnerWrapper, MasteryCountCellText, SubjectCell, TopicCell, CellIcon, BarCell, BarCellText, CellProgressBar, CellSecondary, CellText, iconMap} from '@bootcamp/web/src/bootcamps/components/Performance/shared';
import CreateTest from '@bootcamp/web/src/bootcamps/pages/CreateTest';
import {TextTooltip} from '../CreateTest/components/shared';

import {useUserDataContext} from '../../../contexts/UserData';
import {useModalContext} from '../../../contexts/Modal';

import {scoringTargets} from './targets'

import {slugify} from '@bootcamp/shared/src/util';

import {AddSquare} from '@styled-icons/fluentui-system-filled/AddSquare';
import {ReactComponent as Info} from '@bootcamp/shared/src/assets/svg/warning.svg';

import theme from '@bootcamp/shared/src/styles/theme';


const Container = styled.div`
`;
const StyledHeading = styled(Heading)`
  padding: 0 32px;
  ${({theme}) => theme.mediaQueries.tablet} {
    padding: ${({parentHasPadding}) => parentHasPadding ? '' : '0 16px'};
  }
`;
const StyledBlurb = styled(Blurb)`
  ${BlurbText} {
    margin: 0;
  }
`;
const StyledLabel = styled(H2)`
  color: white;
`;
const InfoIcon = styled(Info)`
  width: 20px;
  height: 20px;
  color: ${({theme, alert}) => alert ? theme.colors.interfacePalette.red.default : theme.overlays.opacity.light._300};
  margin-right: ${({theme, alert}) => alert ? 0 : theme.layouts.spacing.m};
`;
const ScoreWrapper = styled.span`
  display: flex;
  align-items: center;
`;
const InfoIconWrapper = styled.span`
  position: relative;
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 10px;
  margin: 0px 4px;

  ${InfoIcon} {
    position: absolute;
    width: 16px;
    height: 16px;
    top: -2px;
    left: -3px;
    bottom: 0;
    right: 0;
  }
`;
const StyledTable = styled(Table)`
  border-radius: 8px;
  overflow: hidden;
  background: white;

  ${TableRow} {
    svg {
      width: 16px;
    }
  }
`;

const StyledAddSquare = styled(AddSquare)`
  width: 16px;
  height: 16px;
  color: white;
`;
const StyledTopicCell = styled(TopicCell)`
  color: ${({theme}) => theme.colors.typographyPalette.primary};
`;
const RowButtonContainer = styled.div`
  width: 32px;
  height: 32px;
  display: flex;
  justify-content: center;
  align-items: center;
  background: ${({theme}) => theme.colors.brandPalette.royal.default};
  border-radius: 50%;
  cursor: pointer;

  &:hover {
    background: ${({theme}) => theme.colors.brandPalette.royal.hover};
    transform: translateY(-1px);
  }
`;

const ButtonElement = ({onClick, disabled}) => {

  return (
    <RowButtonContainer onClick={onClick} disabled={disabled}>
      <StyledAddSquare />
    </RowButtonContainer>
  );
};


export const AlertIcon = () => (
  <InfoIconWrapper>
    <InfoIcon alert/>
  </InfoIconWrapper>
);

const Subject = ({performanceData, educator, config, hideHeader=false, defaultExpanded=null, headerText, headerTitle, loading, parentHasPadding}) => {
  const {bootcamp} = useUserDataContext();
  const {modalDispatch} = useModalContext();
  const history = useHistory();
  const tableWrapperRef = useRef();

  const {byClassroom: performance, overall} = performanceData;

  function handleRowClick(event, subject, topic) {

    event.preventDefault();
    event.stopPropagation();

    if (['nclex', 'med-school', 'inbde'].includes(bootcamp) && !['Case Sets', 'Simulation Exam'].includes(subject)) {
      modalDispatch({
        type: 'open',
        component: CreateTest,
        componentProps: {defaults: {subject, system: topic}},
        enableClickClose: true,
      });
    } else if (bootcamp === 'inbde' && subject === 'Case Sets') {
      history.push(`/${bootcamp}/${slugify(subject)}/qbanks/case-set-1`);
    } else if (bootcamp === 'inbde' && subject === 'Simulation Exam') {
      history.push(`/${bootcamp}/${slugify(subject)}/qbanks`);
    } else {
      history.push(`/${bootcamp}/${slugify(subject)}/qbanks/${slugify(topic)}`);
    }
  }


  const rows = performance
    // .filter(({name, mastery, performance, byTopic, totalQuestions}) => !!name && !!Object.keys(byTopic || {}).length)
    .map(({name, mastery, performance, byTopic, displayTopics, totalQuestions, route}) => {
      const rawPercentage = Math.round((performance?.correct / performance?.attempts) * 100) || 0;
      const subjectPercentage = totalQuestions * (rawPercentage / 100)

      const subjectTargetScore = Math.round(Object.values(scoringTargets?.[bootcamp]?.[name] || {})?.reduce((acc, val) => acc + val, 0) / Object.keys((scoringTargets?.[bootcamp]?.[name] || {}))?.length);

      const subjectTargetTagged = Math.round(totalQuestions * .85);

      const subjectScoreSegments = [
        {count: subjectPercentage, color: theme.colors.brandPalette.blue.gradient},
      ];

      const subjectMasterySegments = [
        {count: mastery?.mastered, spacing: 0},
        {count: mastery?.reviewing, spacing: 0},
        {count: mastery?.learning, spacing: 0},
      ];

      const stillCollecting = (performance?.attempts || 0) < 10;
      const collectingCountdown = 10 - (performance?.attempts || 0);

      return {
        onClick: (event) => handleRowClick(event, name),
        parent: [
          {content: (
          <SubjectCell>
            <CellIcon src={iconMap?.[bootcamp]?.[name]} />
            <CellText>{name}</CellText>
          </SubjectCell>
          )},
          {content: (
            <BarCell>
              {stillCollecting
                ? `Answer ${collectingCountdown} more questions`
                : <>
                  <BarCellText>
                    {rawPercentage}%
                  </BarCellText>
                <CellProgressBar segments={subjectScoreSegments} total={totalQuestions} markPosition={subjectTargetScore} loading={loading}/>
                </>
              }
            </BarCell>
          )},
          {content: <CellSecondary>{subjectTargetScore}%</CellSecondary>},
          {content: (
            <BarCell>
              <MasteryCountCellText>
                {loading
                  ? ''
                  : `${mastery?.total || 0} / ${totalQuestions}`
                }
              </MasteryCountCellText>
              <CellProgressBar segments={subjectMasterySegments} total={totalQuestions} markPosition={75} loading={loading}/>
            </BarCell>
          )},
          {content: (
            <CellSecondary>
              {loading
                ? ''
                : `${subjectTargetTagged} / ${totalQuestions}`
              }
              </CellSecondary>
          )},
        ],
        nested: displayTopics
          // .filter(({name: topicName, performance}) => !!topicName && !!Object.keys(performance).length)
          ?.sort((a, b) => a.name?.localeCompare(b.name))
          ?.map(({name: topicName, totalQuestions}) => {
            const {performance, mastery} = Object.values(byTopic).find(({name}) => name === topicName) || {};
            const scorePercentage = Math.round((performance?.correct / performance?.attempts) * 100) || 0;
            const topicPercentage = totalQuestions * (scorePercentage / 100);

            const topicExists = config.find(({name: subject}) => name === subject)?.contentTypes?.find(({name}) => ['Board Style Questions'].includes(name))?.content?.[0]?.content?.find(({name}) => name === topicName);

            // the ternary in topicAvgScore variable is to handle missing topic targets for inbde case set and sim exams, can remove once we have those values
            const topicAvgScore = scoringTargets?.[bootcamp]?.[name?.trim()]?.[topicName?.trim()] || 0;
            const topicAvgTagged = Math.round(totalQuestions * .85);

            const collectionBoundary = Math.round(Math.min(10, totalQuestions * .5));
            const stillCollecting = (performance?.attempts || 0) < collectionBoundary;
            const collectingCountdown = collectionBoundary - (performance?.attempts || 0);

            const topicScoreSegments = [
              {count: topicPercentage, color: theme.colors.brandPalette.blue.gradient}
            ];

            const topicMasterySegments = [
              {count: mastery?.mastered, spacing: 0},
              {count: mastery?.reviewing, spacing: 0},
              {count: mastery?.learning, spacing: 0},
            ];

            // TODO figure out what to do with progress bar displays for deprecated topics
            if (bootcamp === 'med-school' && !topicExists) return {
              noHover: true,
              cells: [
                {content: ''},
                {
                  content: (
                    <StyledTopicCell>
                      {topicName}
                    </StyledTopicCell>
                  ),
                  disabled: true
                },
                {content: (
                  <BarCell>
                    {/* <BarCellText>{scorePercentage}%</BarCellText> */}
                    {/* <CellProgressBar segments={topicScoreSegments} total={totalQuestions} markPosition={topicAvgScore}/> */}
                  </BarCell>
                )},
                {content: (
                  <CellSecondary>
                    {/* {topicAvgScore}% */}
                  </CellSecondary>
                )},
                {content: (
                  <BarCell>
                    {/* <MasteryCountCellText>{mastery?.total} / {totalQuestions}</MasteryCountCellText> */}
                    {/* <CellProgressBar segments={topicMasterySegments} total={totalQuestions} markPosition={75}/> */}
                  </BarCell>
                )},
                {content: (
                  <CellSecondary>
                    {/* {topicAvgTagged} / {totalQuestions} */}
                  </CellSecondary>
                )},
              ]
            }

            return {
              onClick: (event) => handleRowClick(event, name, topicName),
              noHover: true,
              cells: [
                {content: ''},
                {
                  content: (
                    <StyledTopicCell>{topicName} {scorePercentage < 50 && !stillCollecting && <AlertIcon/>}</StyledTopicCell>
                  ),
                  disabled: true
                },
                {content: (
                  <BarCell>
                    {stillCollecting
                      ? `Answer ${collectingCountdown} more questions`
                      : <>
                        <BarCellText>
                          {scorePercentage}%
                        </BarCellText>
                        <CellProgressBar segments={topicScoreSegments} total={totalQuestions} markPosition={topicAvgScore}/>
                      </>
                    }
                  </BarCell>
                )},
                {content: <CellSecondary>{topicAvgScore}%</CellSecondary>},
                {content: (
                  <BarCell>
                    <MasteryCountCellText>{mastery?.total|| 0} / {totalQuestions}</MasteryCountCellText>
                    <CellProgressBar segments={topicMasterySegments} total={totalQuestions} markPosition={75}/>
                  </BarCell>
                )},
                {content: <CellSecondary>{topicAvgTagged} / {totalQuestions}</CellSecondary>},
              ]
            };
          })
      };
    });

  const headerRows = [{ content: '' }, { content: bootcamp === 'med-school' ? 'System' : 'Subject' }, { content: 'Score' }, {content: 'Target', disabled: true}, {content: 'Questions Tagged'}, {content: 'Target', disabled: true}, {content: ''}];

  const title = bootcamp === 'med-school'
    ? 'Step 1 - System Pass Targets'
    : 'Subject Pass Targets';

  const subtitleFragment = bootcamp === 'med-school'
    ? 'Increase your score in each system by focusing on subjects'
    : bootcamp === 'nclex'
    ? 'Increase your score in each subject by focusing on systems'
    : 'Increase your score in each subject by focusing on topics';

  return (
    <Container>
      <StyledHeading parentHasPadding={parentHasPadding}>
        {headerTitle && <StyledLabel>{headerTitle}</StyledLabel>}
        {!headerTitle && <StyledLabel>{title}</StyledLabel>}
        <SubTitle>{subtitleFragment} marked with a red <AlertIcon/> - that's where you can improve the most!</SubTitle>
      </StyledHeading>
      <AutoHeightParent childRef={tableWrapperRef}>
        <TableOverflowOuterWrapper parentHasPadding={parentHasPadding}>
          <TableOverflowInnerWrapper ref={tableWrapperRef} parentHasPadding={parentHasPadding} >
            <StyledTable
              expandAll={defaultExpanded}
              fixed={false}
              buttonElement={ButtonElement}
              loading={loading}
              rows={[
                {cells: [
                ...headerRows,
                // {content: (
                //   <ScoreWrapper>
                //     Score
                //     <TextTooltip id={'performance-score-tooltip'}>
                //       <strong>Score</strong> tracks every question attempt including reattempts.
                //     </TextTooltip>
                //   </ScoreWrapper>)},
                ]},
                ...rows
              ]}
            />
          </TableOverflowInnerWrapper>
        </TableOverflowOuterWrapper>
      </AutoHeightParent>
    </Container>
  );
};

Subject.defaultProps = {};
Subject.propTypes = {};

export default Subject;